import 'react-quill-new/dist/quill.snow.css';
import './FormattedTextInput.scss';

import dayjs from 'dayjs';
import { ToolbarConfig } from 'quill/modules/toolbar';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';

import { rehydrateQuillLists } from '@/utils/rehydrateQuillLists';

const toolbarOptions: ToolbarConfig = [
  ['bold', 'italic', 'underline', 'strike'], // toggled buttons
  [{ header: [1, 2, 3, false] }],
  ['link', 'image'],

  [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
  [{ indent: '-1' }, { indent: '+1' }], // outdent/indent

  [{ color: [] }, { background: [] }], // dropdown with defaults from theme
  [{ align: [] }],

  ['clean'], // remove formatting button
];

type QuillEditor = ReturnType<ReactQuill['getEditor']>;

const EFFECTIVELY_EMPTY_LINE_PATTERN = /[\n\r\t \u00A0\u200B\uFEFF]/g;

/**
 * @description Detecta lineas de lista visualmente vacias aunque Quill no marque
 * `context.empty`, algo que puede pasar cuando el HTML controlado fue normalizado.
 */
function isEffectivelyEmptyListLine(editor: QuillEditor, index: number, offset: number): boolean {
  const [line] = editor.getLine(index);

  if (!line) {
    return false;
  }

  const lineStart = Math.max(0, index - offset);
  const normalizedLineText = editor
    .getText(lineStart, line.length())
    .replace(EFFECTIVELY_EMPTY_LINE_PATTERN, '');

  return normalizedLineText.length === 0;
}

interface FormattedTextInputProps {
  value?: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
}

const FormattedTextInput = (props: FormattedTextInputProps) => {
  const { value, onChange, readOnly } = props;
  const quillRef = useRef<ReactQuill | null>(null);
  const hasUserInteractedRef = useRef(false);
  const isPointerSelectingRef = useRef(false);
  // Rehidrata metadata de listas de Quill en la entrada del componente para centralizar el fallback.
  const hydratedValue = useMemo(() => rehydrateQuillLists(value), [value]);
  const [editorValue, setEditorValue] = useState(hydratedValue);

  useEffect(() => {
    // Sincroniza cambios externos para mantener el editor controlado sin perder normalizaciones internas.
    setEditorValue(hydratedValue);
  }, [hydratedValue]);

  const handleEditorFocus = useCallback(() => {
    hasUserInteractedRef.current = true;
  }, []);
  const insertTimestamp = useCallback(() => {
    const editor = quillRef.current?.getEditor();

    if (!editor) {
      return;
    }

    const range = editor.getSelection(true);
    const timestamp = dayjs().format('DD/MM/YYYY HH:mm');
    const insertAt = (() => {
      if (range) {
        if (range.length > 0) {
          editor.deleteText(range.index, range.length, 'user');
        }

        return range.index;
      }

      return Math.max(0, editor.getLength() - 1);
    })();

    editor.insertText(insertAt, `${timestamp}: `, { bold: true }, 'user');
    editor.setSelection(insertAt + timestamp.length + 2, 0, 'user');
    editor.format('bold', false, 'user');
  }, []);
  const modules = useMemo(() => {
    if (readOnly) {
      return { toolbar: false };
    }

    return {
      toolbar: toolbarOptions,
      keyboard: {
        bindings: {
          exitListOnEffectiveEmptyLine: {
            key: 'Enter',
            collapsed: true,
            format: ['list'],
            handler: function (
              this: { quill: QuillEditor },
              range: { index: number; length: number },
              context: { offset: number; format: { indent?: unknown } },
            ) {
              if (!isEffectivelyEmptyListLine(this.quill, range.index, context.offset)) {
                return true;
              }

              const formats: { list: false; indent?: false } = { list: false };

              if (context.format.indent) {
                formats.indent = false;
              }

              // Fuerza la salida de la lista antes del handler por defecto cuando
              // Quill no reconoce la linea como vacia tras el round-trip de HTML.
              this.quill.formatLine(range.index, range.length, formats, 'user');

              return false;
            },
          },
          insertTimestamp: {
            key: 'd',
            shortKey: true,
            handler: () => {
              insertTimestamp();

              return false;
            },
          },
        },
      },
    };
  }, [insertTimestamp, readOnly]);
  const formats = useMemo(
    () => [
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'link',
      'image',
      'list',
      'indent',
      'color',
      'background',
      'align',
    ],
    [],
  );
  const handleChange = useCallback(
    (content: string, _delta: unknown, source: string) => {
      // Mantiene alineado el valor del editor incluso cuando Quill normaliza HTML (source="api").
      setEditorValue(previousValue => (previousValue === content ? previousValue : content));

      if (source !== 'user') {
        return;
      }

      hasUserInteractedRef.current = true;
      onChange?.(content);
    },
    [onChange],
  );

  useEffect(() => {
    if (readOnly) {
      return;
    }

    const editor = quillRef.current?.getEditor();
    const root = editor?.root;

    if (!root) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== 0) {
        return;
      }

      isPointerSelectingRef.current = true;
    };

    const handlePointerUp = () => {
      isPointerSelectingRef.current = false;
    };

    const handleScrollIntent = () => {
      const currentEditor = quillRef.current?.getEditor();

      if (!currentEditor?.hasFocus() || isPointerSelectingRef.current) {
        return;
      }

      // Al perder foco durante el scroll evitamos que el navegador/Quill
      // reposicionen la vista para mantener visible el caret.
      hasUserInteractedRef.current = true;
      currentEditor.blur();
    };

    root.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('pointerup', handlePointerUp, { capture: true });
    window.addEventListener('pointercancel', handlePointerUp, { capture: true });
    document.addEventListener('wheel', handleScrollIntent, { capture: true, passive: true });
    document.addEventListener('touchmove', handleScrollIntent, { capture: true, passive: true });

    return () => {
      root.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('pointerup', handlePointerUp, true);
      window.removeEventListener('pointercancel', handlePointerUp, true);
      document.removeEventListener('wheel', handleScrollIntent, true);
      document.removeEventListener('touchmove', handleScrollIntent, true);
    };
  }, [readOnly]);

  useEffect(() => {
    if (hasUserInteractedRef.current) {
      return;
    }

    const editor = quillRef.current?.getEditor();
    const root = editor?.root;

    if (!root) {
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      if (root.scrollHeight > root.clientHeight) {
        root.scrollTop = root.scrollHeight;
      }
    });

    return () => window.cancelAnimationFrame(rafId);
  }, [editorValue]);

  return (
    <div className="FormattedTextInput">
      <ReactQuill
        theme="snow"
        value={editorValue}
        onChange={handleChange}
        onFocus={handleEditorFocus}
        modules={modules}
        formats={formats}
        readOnly={readOnly}
        // Mantiene el HTML interno de Quill para evitar perdidas de metadata en listas
        // durante el ciclo controlado de `value`/`onChange`.
        useSemanticHTML={false}
        ref={quillRef}
      />
    </div>
  );
};

export default FormattedTextInput;
